export const XO_INPUT_SCHEMA = "xo.input.v1" as const;
export type XOInputIntent = "preserve" | "tell" | "send" | "make" | "share" | "understand";
export type XOInputStage = "inbox" | "vault" | "ledger" | "digest";
export type XOInputStageStatus = "confirmed" | "pending" | "processing" | "failed";

export type XOInputProvenance = {
  conversation_record_id: string;
  conversation_schema: "xo.conversation_record.v47";
};

export type XOInputStageReceipt = {
  stage: XOInputStage;
  status: XOInputStageStatus;
  receipt_id: string | null;
  external_ref: string | null;
  attempts: number;
  last_error: string | null;
  updated_at: string;
};

export type XOInputRecord = {
  schema: typeof XO_INPUT_SCHEMA;
  id: string;
  intent: XOInputIntent;
  source: "human";
  created_at: string;
  cornerstone_id?: string;
  source_ref?: XOInputProvenance;
  stages: XOInputStageReceipt[];
};

export type XOKPIMeasurement = { id: string; value: number | null; unit: "count" | "percent" | "hours" };

export type XOGamesMeasurementEvent = {
  event_name: "proof_view" | "proof_share_intent" | "proof_referral_arrival";
  proof_ref: string;
  source_class: "community" | "creator" | "press" | "grader" | "publisher" | "other_organic" | "unknown";
  source_ref?: string | null;
  is_paid: boolean;
};

export const XO_INPUT_ROUTE: ReadonlyArray<{ stage: XOInputStage; href: string }> = [
  { stage: "inbox", href: "/input" },
  { stage: "vault", href: "/vault" },
  { stage: "ledger", href: "/ledger" },
  { stage: "digest", href: "/digest" },
];

export const INTENT_CORNERSTONE: Record<XOInputIntent, string> = {
  preserve: "inbox",
  tell: "inbox",
  send: "inbox",
  make: "inbox",
  share: "inbox",
  understand: "inbox",
};

function inputOrigin(): string {
  const configured = (import.meta.env.VITE_XO_INPUT_API_ORIGIN ?? "").trim().replace(/\/+$/, "");
  return configured || "https://inbox-21xo.fly.dev";
}

function idempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function responseJson<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as T | { detail?: string } | null;
  if (!response.ok) throw new Error((payload as { detail?: string } | null)?.detail || `XO Input failed (${response.status})`);
  return payload as T;
}

type SubmitOptions = {
  idempotencyKey?: string;
  provenance?: XOInputProvenance;
};

export async function submitXOInput(intent: XOInputIntent, body: string, options?: SubmitOptions): Promise<XOInputRecord> {
  const clean = body.trim();
  if (!clean) throw new Error("Input is required");
  const payload: Record<string, unknown> = { intent, body: clean, source: "human" };
  if (options?.provenance) payload.source_ref = options.provenance;
  const response = await fetch(`${inputOrigin()}/api/input`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": options?.idempotencyKey ?? idempotencyKey(),
    },
    body: JSON.stringify(payload),
  });
  const result = await responseJson<{ input: XOInputRecord }>(response);
  return result.input;
}

export async function submitGamesMeasurement(
  event: XOGamesMeasurementEvent,
  options?: { idempotencyKey?: string },
): Promise<{ created: boolean }> {
  const response = await fetch(`${inputOrigin()}/api/games-measurement`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": options?.idempotencyKey ?? idempotencyKey(),
    },
    body: JSON.stringify(event),
  });
  return responseJson<{ created: boolean }>(response);
}

export async function fetchXOInput(inputId: string): Promise<XOInputRecord> {
  const response = await fetch(`${inputOrigin()}/api/input/${encodeURIComponent(inputId)}`);
  return (await responseJson<{ input: XOInputRecord }>(response)).input;
}

export async function fetchXOInputMetrics(): Promise<XOKPIMeasurement[]> {
  const response = await fetch(`${inputOrigin()}/api/input-metrics`);
  return (await responseJson<{ measurements: XOKPIMeasurement[] }>(response)).measurements;
}
